import React, { useCallback, useEffect, useMemo, useState } from "react";
import { appointmentService, googleCalendarService } from "../services/dataService";
import { getRoles } from "../services/api";
import { usePermissions } from "../context/PermissionContext";
import { useSettings } from "../context/SettingsContext";
import {
  Appointment,
  AppointmentCalendar,
  AppointmentType,
  AvailabilityExceptionRow,
  CalendarBlock,
  StaffAvailabilityRow,
  StaffBookingProfile,
  GoogleAccountStatus,
  AppointmentPeople,
} from "../types";
import PageLoader from "../components/PageLoader";
import ConfirmModal from "../components/ConfirmModal";
import AppointmentDetailModal from "../components/AppointmentDetailModal";
import AppointmentReasonModal from "../components/AppointmentReasonModal";
import AppointmentTypeModal from "../components/AppointmentTypeModal";
import CalendarBlockModal from "../components/CalendarBlockModal";
import "./PurchaseOrders.css";
import "./Appointments.css";

/**
 * Randevu ve Takvim (appointments modülü) — po-* (kurumsal lacivert) dili.
 *
 * Dört sekme: Takvim · Onay bekleyenler · Randevu sayfam · Randevu türleri.
 * Saatler her yerde TSİ; sunucu UTC gönderir, gösterim tarayıcının yerel
 * saatiyle yapılır ve başlıkta bunun TSİ olduğu açıkça yazılır.
 */

const DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const SHORT = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00 – 19:00

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const startOfWeek = (d: Date) => {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7; // Pazartesi = 0
  x.setDate(x.getDate() - wd);
  x.setHours(0, 0, 0, 0);
  return x;
};

const hhmm = (s: string) =>
  new Date(s).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

const dayLabel = (d: Date) =>
  d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });

/**
 * datetime-local alanı YEREL saat ister, sunucu UTC bekler. Çevrim tek yerde
 * dursun: doğrudan toISOString().slice(0,16) yazmak saati TSİ farkı kadar
 * kaydırır ve meşguliyet yanlış saate düşer.
 */
const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (s: string) => new Date(s).toISOString();

/** Takvim pili rengi (ap-ev): 2 onay bekliyor · 3 onaylı · 6 tamamlandı · 7 gelinmedi · 1 doğrulama */
const EV_CLASS: Record<number, string> = { 1: "muted", 2: "pending", 3: "confirmed", 6: "done", 7: "noshow" };

const MODE_LABEL: Record<number, string> = { 1: "Yüz yüze", 2: "Çevrim içi", 3: "Yüz yüze veya çevrim içi" };

type Tab = "calendar" | "pending" | "hours" | "types";

const I = {
  prev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 6-6 6 6 6" /></svg>,
  next: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>,
  link: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4.5 4.5L19 7" /></svg>,
  google: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /><path d="m9.5 15.5 2 2 3.5-4" /></svg>,
};

const Appointments: React.FC = () => {
  const { has } = usePermissions();
  const { settings } = useSettings();
  const canSettings = has("appointments:settings");
  const canUpdate = has("appointments:update");

  const [tab, setTab] = useState<Tab>("calendar");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  // Kimin takvimi: "me" | "team" (İK'da bana bağlı ekip + ben) | kullanıcı kimliği
  const [who, setWho] = useState<string>("me");
  const [people, setPeople] = useState<AppointmentPeople | null>(null);
  useEffect(() => { appointmentService.people().then(setPeople).catch(() => setPeople(null)); }, []);
  const [cal, setCal] = useState<AppointmentCalendar>({ appointments: [], blocks: [] });
  const [pending, setPending] = useState<Appointment[]>([]);
  const [detail, setDetail] = useState<Appointment | null>(null);
  const [reasonAsk, setReasonAsk] = useState<{ kind: "reject" | "cancel"; a: Appointment } | null>(null);

  const [profile, setProfile] = useState<StaffBookingProfile | null>(null);
  const [hours, setHours] = useState<StaffAvailabilityRow[]>([]);
  const [exceptions, setExceptions] = useState<AvailabilityExceptionRow[]>([]);
  const [excDraft, setExcDraft] = useState<{ date: string; reason: string }>({ date: iso(new Date()), reason: "" });
  const [types, setTypes] = useState<AppointmentType[]>([]);
  const [roles, setRoles] = useState<{ roleId: number; name: string; userCount?: number }[]>([]);
  const [editType, setEditType] = useState<AppointmentType | null>(null);
  const [blockDraft, setBlockDraft] = useState<CalendarBlock | null>(null);
  const [google, setGoogle] = useState<GoogleAccountStatus | null>(null);
  const [confirm, setConfirm] = useState<{ isOpen: boolean; title: string; message: string; type: "info" | "warning" | "danger"; confirmText: string; onConfirm: () => void }>({ isOpen: false, title: "", message: "", type: "info", confirmText: "Onayla", onConfirm: () => {} });
  const closeConfirm = () => setConfirm((c) => ({ ...c, isOpen: false }));

  const flash = (m: string) => {
    setOk(m);
    window.setTimeout(() => setOk(null), 3000);
  };
  const fail = (e: any, fallback: string) =>
    setErr(e?.response?.data?.message || e?.message || fallback);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart],
  );

  const loadCalendar = useCallback(async () => {
    const from = new Date(weekStart);
    const to = new Date(weekStart);
    to.setDate(to.getDate() + 7);
    try {
      setCal(await appointmentService.calendar(from.toISOString(), to.toISOString(), who === "me" || who === "team" ? undefined : who, who === "team" ? "team" : undefined));
    } catch (e) {
      fail(e, "Takvim yüklenemedi.");
    }
  }, [weekStart, who]);

  const loadAll = useCallback(async () => {
    try {
      const [p, h, t, pend] = await Promise.all([
        appointmentService.profile(),
        appointmentService.availability(),
        appointmentService.types(),
        appointmentService.pending(),
      ]);
      setProfile(p);
      setHours(h);
      setTypes(t);
      setPending(pend);
      if (canSettings) {
        try {
          setRoles((await getRoles()).data ?? []);
        } catch {
          /* rol listesi alınamazsa tür ekranı yine çalışır */
        }
      }
    } catch (e) {
      fail(e, "Randevu verileri yüklenemedi.");
    }
  }, [canSettings]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadAll();
      setLoading(false);
    })();
  }, [loadAll]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const refresh = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await Promise.all([loadAll(), loadCalendar(), googleCalendarService.status().then(setGoogle).catch(() => {})]);
    } finally {
      setBusy(false);
    }
  };

  // Google bağlantı durumu + dönüş sonucu. Google bizi ?google=... ile geri
  // gönderiyor; sonucu bir kez gösterip parametreyi adres çubuğundan siliyoruz
  // ki sayfa yenilendiğinde eski mesaj tekrar çıkmasın.
  useEffect(() => {
    googleCalendarService.status().then(setGoogle).catch(() => {});

    const p = new URLSearchParams(window.location.search).get("google");
    if (!p) return;
    const msg: Record<string, string> = {
      baglandi: "Google Takvim bağlandı. Onayladığınız randevular takviminize düşecek.",
      iptal: "Google bağlantısı iptal edildi.",
      hata: "Google bağlantısı kurulamadı. Lütfen tekrar deneyin.",
      eksik: "Google'dan eksik yanıt geldi. Lütfen tekrar deneyin.",
      gecersiz: "Bağlantı isteğinin süresi dolmuş. Lütfen tekrar deneyin.",
    };
    if (p === "baglandi") flash(msg[p]);
    else setErr(msg[p] ?? "Google bağlantısı tamamlanamadı.");
    setTab("hours");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!profile) return;
    const from = iso(new Date());
    const to = iso(new Date(Date.now() + 90 * 864e5));
    appointmentService.exceptions(undefined, from, to).then(setExceptions).catch(() => {});
  }, [profile]);

  // ---- Takvim yerleşimi ----

  const todayKey = iso(new Date());

  const eventsFor = (day: Date) => {
    const key = iso(day);
    const apts = cal.appointments
      .filter((a) => iso(new Date(a.startAt)) === key && a.status !== 4 && a.status !== 5)
      .map((a) => ({ kind: "apt" as const, a }));
    const blocks = cal.blocks
      .filter((b) => iso(new Date(b.startAt)) === key)
      .map((b) => ({ kind: "block" as const, b }));
    return [...apts, ...blocks].sort((x, y) =>
      (x.kind === "apt" ? x.a.startAt : x.b.startAt).localeCompare(
        y.kind === "apt" ? y.a.startAt : y.b.startAt,
      ),
    );
  };

  const eventsAt = (day: Date, hour: number) =>
    eventsFor(day).filter((e) => {
      const s = new Date(e.kind === "apt" ? e.a.startAt : e.b.startAt);
      return s.getHours() === hour;
    });

  // Hafta özeti (yüklü veriden; ek istek yok)
  const weekLive = cal.appointments.filter((a) => a.status !== 4 && a.status !== 5);
  const weekPending = weekLive.filter((a) => a.status === 2).length;
  const todayInWeek = weekDays.some((d) => iso(d) === todayKey);
  const todayCount = weekLive.filter((a) => iso(new Date(a.startAt)) === todayKey).length;
  const weekEmpty = weekLive.length === 0 && cal.blocks.length === 0;

  /**
   * Kurulum listesi. Her adım GERÇEK duruma bakar, kullanıcının "yaptım" demesine
   * değil: yarım kalmış bir kurulumda çalışan bağlantısını paylaşıp randevu
   * gelmeyince nedenini bulamıyordu.
   */
  const myTypes = types.filter(
    (t) => profile?.myRoleId != null && t.roleIds.includes(profile.myRoleId) && t.isPublic,
  );
  const setupSteps = [
    {
      short: "Sayfa",
      title: "Randevu sayfanızı oluşturun",
      done: !!profile?.exists,
      todo: "Aşağıdaki “Randevu sayfanız” bölümünü doldurup kaydedin. Bağlantı adı adresinizin sonundaki kısımdır.",
      doneText: profile?.publicUrl ?? "Kaydedildi.",
    },
    {
      short: "Saatler",
      title: "Çalışma saatlerinizi girin",
      done: hours.length > 0,
      todo: "Hangi gün ve saatlerde randevu kabul ettiğinizi işaretleyin. Saat girilmeyen günde randevu alınamaz.",
      doneText: `${hours.length} gün açık.`,
    },
    {
      short: "Tür",
      title: "Rolünüze randevu türü tanımlı olsun",
      done: myTypes.length > 0,
      todo:
        profile?.myRoleId == null
          ? "Kullanıcınıza rol atanmamış görünüyor. Yöneticinizden rol tanımlamasını isteyin."
          : "Türler role bağlanır ve yalnız yetkili kişi tanımlar. Yöneticinizden rolünüze en az bir randevu türü işaretlemesini isteyin.",
      doneText: myTypes.map((t) => t.name).join(", "),
    },
    ...(google?.configured
      ? [
          {
            short: "Google",
            title: "Google Takvim’e bağlanın",
            done: !!google?.connected && !google?.lastError,
            todo:
              "Bağlarsanız her randevu takviminize düşer ve o randevuya özel Meet bağlantısı üretilir. Bağlamazsanız profilinizdeki sabit oda kullanılır.",
            doneText: google?.googleEmail ?? "Bağlı.",
          },
        ]
      : []),
    {
      short: "Yayın",
      title: "Sayfanızı yayına alın",
      done: !!profile?.isPublic && !!settings?.publicBookingEnabled,
      todo: !settings?.publicBookingEnabled
        ? "Genel randevu sayfası sistem genelinde kapalı. Yöneticinizden Ayarlar › Randevu Sayfası bölümünden açmasını isteyin."
        : "Aşağıdaki “Yayın durumu” anahtarını Yayında konumuna alın.",
      doneText: "Sayfanız dışarıdan açılabiliyor.",
    },
  ];
  const setupDone = setupSteps.filter((s) => s.done).length;
  const setupCurrent = setupSteps.findIndex((s) => !s.done);

  // ---- İşlemler ----

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const approve = (a: Appointment) =>
    run(async () => {
      try {
        await appointmentService.approve(a.appointmentId);
        flash("Randevu onaylandı, misafire takvim daveti gönderildi.");
      } catch (e) {
        return fail(e, "Onay işlemi başarısız.");
      }
      setDetail(null);
      setPending(await appointmentService.pending());
      loadCalendar();
    });

  const reject = (a: Appointment, reason: string) =>
    run(async () => {
      try {
        await appointmentService.reject(a.appointmentId, reason);
        flash("Talep reddedildi, misafire bildirildi.");
      } catch (e) {
        return fail(e, "Ret işlemi başarısız.");
      }
      setReasonAsk(null);
      setDetail(null);
      setPending(await appointmentService.pending());
      loadCalendar();
    });

  const cancelAppointment = (a: Appointment, reason: string) =>
    run(async () => {
      try {
        await appointmentService.setStatus(a.appointmentId, 5, reason);
        flash("Randevu iptal edildi, misafire bildirildi.");
        setReasonAsk(null);
        setDetail(null);
        setPending(await appointmentService.pending());
        loadCalendar();
      } catch (e) {
        fail(e, "İptal başarısız.");
      }
    });

  const markStatus = (a: Appointment, status: 6 | 7) =>
    run(async () => {
      try {
        await appointmentService.setStatus(a.appointmentId, status);
        flash(status === 6 ? "Tamamlandı olarak işaretlendi." : "Gelinmedi olarak işaretlendi.");
        setDetail(null);
        loadCalendar();
      } catch (e) {
        fail(e, "İşlem başarısız.");
      }
    });

  const saveProfile = () =>
    run(async () => {
      if (!profile) return;
      try {
        const r = await appointmentService.saveProfile(profile);
        setProfile({ ...profile, slug: r.slug, publicUrl: r.publicUrl });
        flash("Randevu sayfanız kaydedildi.");
      } catch (e) {
        fail(e, "Profil kaydedilemedi.");
      }
    });

  const saveHours = () =>
    run(async () => {
      try {
        await appointmentService.saveAvailability(hours);
        flash("Çalışma saatleri kaydedildi.");
        loadCalendar();
      } catch (e) {
        fail(e, "Çalışma saatleri kaydedilemedi.");
      }
    });

  const setDayHours = (dow: number, on: boolean) => {
    setHours((prev) =>
      on
        ? [...prev, { dayOfWeek: dow, startTime: "09:00", endTime: "18:00" }]
        : prev.filter((h) => h.dayOfWeek !== dow),
    );
  };

  const patchDay = (dow: number, patch: Partial<StaffAvailabilityRow>) =>
    setHours((prev) => prev.map((h) => (h.dayOfWeek === dow ? { ...h, ...patch } : h)));

  const addException = () =>
    run(async () => {
      if (!excDraft.date) return;
      try {
        await appointmentService.saveException({ date: excDraft.date, isClosed: true, reason: excDraft.reason });
        setExceptions(
          await appointmentService.exceptions(undefined, iso(new Date()), iso(new Date(Date.now() + 90 * 864e5))),
        );
        setExcDraft({ date: iso(new Date()), reason: "" });
        flash("Gün kapatıldı.");
        loadCalendar();
      } catch (e) {
        fail(e, "Gün kapatılamadı.");
      }
    });

  const removeException = (id?: string) =>
    run(async () => {
      if (!id) return;
      try {
        await appointmentService.removeException(id);
        setExceptions((prev) => prev.filter((x) => x.availabilityExceptionId !== id));
        loadCalendar();
      } catch (e) {
        fail(e, "Silinemedi.");
      }
    });

  const saveType = (draft: AppointmentType) =>
    run(async () => {
      try {
        await appointmentService.saveType(draft);
        setTypes(await appointmentService.types());
        setEditType(null);
        flash("Randevu türü kaydedildi.");
      } catch (e) {
        fail(e, "Tür kaydedilemedi.");
      }
    });

  const removeType = (t: AppointmentType) => {
    if (!t.appointmentTypeId) return;
    setConfirm({
      isOpen: true,
      title: "Türü kaldır",
      message: `“${t.name}” türü kaldırılsın mı? Geçmiş randevular silinmez.`,
      type: "danger",
      confirmText: "Kaldır",
      onConfirm: () => {
        closeConfirm();
        run(async () => {
          try {
            await appointmentService.removeType(t.appointmentTypeId!);
            setTypes(await appointmentService.types());
            flash("Randevu türü kaldırıldı.");
          } catch (e) {
            fail(e, "Tür kaldırılamadı.");
          }
        });
      },
    });
  };

  const saveBlock = (draft: CalendarBlock) =>
    run(async () => {
      try {
        await appointmentService.saveBlock({
          ...draft,
          startAt: fromLocalInput(draft.startAt),
          endAt: fromLocalInput(draft.endAt),
        });
        setBlockDraft(null);
        flash("Meşguliyet takvime eklendi.");
        loadCalendar();
      } catch (e) {
        fail(e, "Eklenemedi.");
      }
    });

  const removeBlock = (b: CalendarBlock) => {
    if (!b.calendarBlockId || !canUpdate) return;
    setConfirm({
      isOpen: true,
      title: "Meşguliyeti kaldır",
      message: `“${b.title}” (${hhmm(b.startAt)} – ${hhmm(b.endAt)}) takvimden kaldırılsın mı? Saat yeniden randevuya açılır.`,
      type: "warning",
      confirmText: "Kaldır",
      onConfirm: () => {
        closeConfirm();
        run(async () => {
          try {
            await appointmentService.removeBlock(b.calendarBlockId!);
            flash("Meşguliyet kaldırıldı.");
            loadCalendar();
          } catch (e) {
            fail(e, "Kaldırılamadı.");
          }
        });
      },
    });
  };

  const connectGoogle = async () => {
    try {
      // Aynı sekmede gidiyoruz: Google izin ekranı pop-up engelleyicilere takılır.
      window.location.href = await googleCalendarService.connectUrl();
    } catch (e) {
      fail(e, "Google bağlantısı başlatılamadı.");
    }
  };

  const disconnectGoogle = () =>
    setConfirm({
      isOpen: true,
      title: "Google bağlantısını kaldır",
      message: "Google Takvim bağlantısı kaldırılsın mı? Yeni randevular için Meet bağlantısı üretilmez.",
      type: "warning",
      confirmText: "Kaldır",
      onConfirm: () => {
        closeConfirm();
        run(async () => {
          try {
            await googleCalendarService.disconnect();
            setGoogle(await googleCalendarService.status());
            flash("Google bağlantısı kaldırıldı.");
          } catch (e) {
            fail(e, "Bağlantı kaldırılamadı.");
          }
        });
      },
    });

  const copyLink = async () => {
    if (!profile?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(profile.publicUrl);
      flash("Bağlantı kopyalandı.");
    } catch {
      setErr("Kopyalanamadı, bağlantıyı elle seçin.");
    }
  };

  const openBlockModal = () =>
    setBlockDraft({
      title: "",
      startAt: toLocalInput(new Date()),
      endAt: toLocalInput(new Date(Date.now() + 36e5)),
      isAllDay: false,
    });

  if (loading) return <PageLoader message="Randevular yükleniyor..." />;

  const showStaff = who !== "me";

  const eventPill = (e: ReturnType<typeof eventsFor>[number], agenda: boolean) =>
    e.kind === "apt" ? (
      <button
        key={e.a.appointmentId}
        type="button"
        className={`ap-ev ${EV_CLASS[e.a.status] ?? "muted"}`}
        onClick={() => setDetail(e.a)}
        title={`${e.a.guestName} · ${e.a.typeName}${showStaff ? ` · ${e.a.staffName}` : ""}`}
      >
        <b>{hhmm(e.a.startAt)}{agenda ? ` – ${hhmm(e.a.endAt)}` : ""}{showStaff && <em>{e.a.staffName}</em>}</b>
        <span>{e.a.guestName}{agenda ? ` · ${e.a.typeName}` : ""}</span>
      </button>
    ) : (
      <button
        key={e.b.calendarBlockId}
        type="button"
        className={`ap-ev block${canUpdate ? "" : " static"}`}
        onClick={() => removeBlock(e.b)}
        title={`${e.b.title} · ${hhmm(e.b.startAt)} – ${hhmm(e.b.endAt)}${canUpdate ? " · kaldırmak için tıklayın" : ""}`}
        disabled={!canUpdate}
      >
        <b>{hhmm(e.b.startAt)}{agenda ? ` – ${hhmm(e.b.endAt)}` : ""}{showStaff && e.b.staffName && <em>{e.b.staffName}</em>}</b>
        <span>{e.b.title}</span>
      </button>
    );

  return (
    <div className="po-page ap-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Randevu</span>
          <h1>Randevu ve Takvim</h1>
          <p>Dışarıdan gelen randevu talepleri, takviminiz ve müsaitlik ayarlarınız. Tüm saatler TSİ.</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={refresh} disabled={busy}><span className={busy ? "po-spin-icon" : ""} style={{ display: "inline-flex" }}>{I.refresh}</span> Yenile</button>
          {profile?.publicUrl && profile.isPublic && (
            <button type="button" className="po-btn" onClick={copyLink}>{I.link} Bağlantıyı kopyala</button>
          )}
          <button type="button" className="po-btn primary" onClick={openBlockModal}>{I.plus} Meşguliyet ekle</button>
        </div>
      </header>

      {err && <div className="po-note err" role="alert">{err} <button type="button" className="po-link" onClick={() => setErr(null)}>Kapat</button></div>}
      {ok && <div className="po-note ok" role="status">{ok}</div>}

      <div className="po-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "calendar"} className={tab === "calendar" ? "on" : ""} onClick={() => setTab("calendar")}>Takvim</button>
        <button type="button" role="tab" aria-selected={tab === "pending"} className={tab === "pending" ? "on" : ""} onClick={() => setTab("pending")}>
          Onay bekleyenler{pending.length > 0 && <span>{pending.length}</span>}
        </button>
        <button type="button" role="tab" aria-selected={tab === "hours"} className={tab === "hours" ? "on" : ""} onClick={() => setTab("hours")}>Randevu sayfam</button>
        {canSettings && (
          <button type="button" role="tab" aria-selected={tab === "types"} className={tab === "types" ? "on" : ""} onClick={() => setTab("types")}>Randevu türleri</button>
        )}
      </div>

      {/* ============ TAKVİM ============ */}
      {tab === "calendar" && (
        <>
          <section className="po-kpis">
            <div className="po-kpi navy"><span className="l">Bu hafta</span><span className="v">{weekLive.length}<small> randevu</small></span><span className="s"><b>{cal.blocks.length}</b> meşguliyet ayrıca</span></div>
            <button type="button" className={`po-kpi ${weekPending > 0 ? "err" : "ok"}`} onClick={() => setTab("pending")}>
              <span className="l">Onay bekleyen</span><span className="v">{pending.length}</span><span className="s">{weekPending > 0 ? `${weekPending} tanesi bu haftada · görmek için tıklayın` : pending.length > 0 ? "bu haftada yok · görmek için tıklayın" : "bekleyen talep yok"}</span>
            </button>
            <div className="po-kpi"><span className="l">Bugün</span><span className="v">{todayInWeek ? todayCount : "–"}</span><span className="s">{todayInWeek ? (todayCount > 0 ? "randevu" : "randevu yok") : "bugün bu haftada değil"}</span></div>
          </section>

          <div className="po-card">
            <div className="po-toolbar ap-toolbar">
              <div className="ap-weeknav">
                <button type="button" className="po-btn small" aria-label="Önceki hafta" onClick={() => setWeekStart((d) => new Date(d.getTime() - 7 * 864e5))}>{I.prev}</button>
                <button type="button" className="po-btn small" onClick={() => setWeekStart(startOfWeek(new Date()))}>Bu hafta</button>
                <button type="button" className="po-btn small" aria-label="Sonraki hafta" onClick={() => setWeekStart((d) => new Date(d.getTime() + 7 * 864e5))}>{I.next}</button>
              </div>
              <span className="ap-range">{dayLabel(weekDays[0])} – {dayLabel(weekDays[6])}<small>{weekDays[0].getFullYear()}</small></span>
              {people && (people.teamCount > 0 || people.canAll) && (
                <label className="ap-who">
                  <span>Takvim</span>
                  <select className="po-select" value={who} onChange={(e) => setWho(e.target.value)}>
                    <option value="me">Benim takvimim</option>
                    {people.teamCount > 0 && <option value="team">Ekibim ({people.teamCount} kişi + ben)</option>}
                    {people.people.filter((p) => p.relation === "team").length > 0 && <optgroup label="Ekibim">{people.people.filter((p) => p.relation === "team").map((p) => <option key={p.userId} value={p.userId}>{p.name}</option>)}</optgroup>}
                    {people.canAll && people.people.filter((p) => p.relation === "all").length > 0 && <optgroup label="Diğer personel">{people.people.filter((p) => p.relation === "all").map((p) => <option key={p.userId} value={p.userId}>{p.name} · {p.role}</option>)}</optgroup>}
                  </select>
                </label>
              )}
            </div>

            {weekEmpty ? (
              <div className="po-blank">
                <span className="po-blank-icon">{I.calendar}</span>
                <b>Bu hafta boş</b>
                {who === "me" ? "Randevu talebi geldiğinde ya da meşguliyet eklediğinizde burada görünür." : "Seçili takvimde bu hafta kayıt yok."}
                <button type="button" className="po-btn" onClick={openBlockModal}>{I.plus} Meşguliyet ekle</button>
              </div>
            ) : (
              <>
                {/* Geniş ekran: saat ızgarası */}
                <div className="ap-cal" role="grid" aria-label="Haftalık takvim">
                  <div className="ap-cal-head">
                    <div className="ap-cal-corner" aria-hidden="true">TSİ</div>
                    {weekDays.map((d) => (
                      <div key={iso(d)} className={`ap-cal-day${iso(d) === todayKey ? " today" : ""}`}>
                        <span>{SHORT[d.getDay()]}</span>
                        <b>{d.getDate()}</b>
                      </div>
                    ))}
                  </div>
                  {HOURS.map((h) => (
                    <div key={h} className="ap-cal-row" role="row">
                      <div className="ap-cal-hour">{String(h).padStart(2, "0")}:00</div>
                      {weekDays.map((d) => (
                        <div key={iso(d) + h} className={`ap-cal-cell${iso(d) === todayKey ? " today" : ""}`} role="gridcell">
                          {eventsAt(d, h).map((e) => eventPill(e, false))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Dar ekran: ajanda */}
                <div className="ap-agenda">
                  {weekDays.map((d) => {
                    const evs = eventsFor(d);
                    return (
                      <div key={iso(d)} className={`ap-agenda-day${iso(d) === todayKey ? " today" : ""}`}>
                        <div className="ap-agenda-head">
                          <b>{d.getDate()}</b>
                          <span>{DAYS[d.getDay()]}{iso(d) === todayKey ? " · bugün" : ""}</span>
                        </div>
                        <div className="ap-agenda-list">
                          {evs.length === 0 && <span className="ap-agenda-none">Randevu yok</span>}
                          {evs.map((e) => eventPill(e, true))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="ap-legend" aria-hidden="true">
            <span><i className="ap-ev pending" /> Onay bekliyor</span>
            <span><i className="ap-ev confirmed" /> Onaylı</span>
            <span><i className="ap-ev done" /> Tamamlandı</span>
            <span><i className="ap-ev noshow" /> Gelinmedi</span>
            <span><i className="ap-ev block" /> Meşguliyet</span>
          </div>
        </>
      )}

      {/* ============ ONAY BEKLEYENLER ============ */}
      {tab === "pending" && (
        <div className="po-card">
          <div className="po-card-head">
            <span><b>{pending.length}</b> talep onayınızı bekliyor</span>
            <span className="po-hide-sm">Onaylarsanız misafire takvim daveti gider; reddederseniz gerekçeniz iletilir ve saat yeniden açılır.</span>
          </div>
          {pending.length === 0 ? (
            <div className="po-blank">
              <span className="po-blank-icon">{I.inbox}</span>
              <b>Onay bekleyen talep yok</b>
              Randevu sayfanızdan gelen yeni talepler burada listelenir.
            </div>
          ) : (
            <div className="po-table-wrap">
              <table className="po-table">
                <thead>
                  <tr>
                    <th>Misafir</th>
                    <th>Konu</th>
                    <th>Tarih ve saat</th>
                    <th className="po-hide-md">İletişim</th>
                    <th className="act">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((a) => (
                    <tr key={a.appointmentId} onClick={() => setDetail(a)}>
                      <td>
                        <div className="po-name">{a.guestName}</div>
                        <div className="po-sub">{a.guestCompany || a.number}{a.guestNote ? <> · <i>“{a.guestNote.length > 60 ? `${a.guestNote.slice(0, 60)}…` : a.guestNote}”</i></> : null}</div>
                      </td>
                      <td>
                        <div className="po-name">{a.typeName}</div>
                        <div className="po-sub">{MODE_LABEL[a.mode]}</div>
                      </td>
                      <td className="po-due">
                        <div className="po-name">{new Date(a.startAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}</div>
                        <div className="po-sub">{DAYS[new Date(a.startAt).getDay()]} · {a.localRange}</div>
                      </td>
                      <td className="po-hide-md">
                        <div>{a.guestEmail}</div>
                        {a.guestPhone && <div className="po-sub">{a.guestPhone}</div>}
                      </td>
                      <td className="act" onClick={(e) => e.stopPropagation()}>
                        {canUpdate ? (
                          <span className="po-rowacts">
                            <button type="button" className="po-btn small danger" onClick={() => setReasonAsk({ kind: "reject", a })} disabled={busy}>Reddet</button>
                            <button type="button" className="po-btn small primary" onClick={() => approve(a)} disabled={busy}>Onayla</button>
                          </span>
                        ) : (
                          <button type="button" className="po-btn small" onClick={() => setDetail(a)}>Aç</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============ RANDEVU SAYFAM ============ */}
      {tab === "hours" && profile && (
        <div className="ap-stack">
          <section className="po-card">
            <div className="ap-body">
              <div className="po-form-sec">
                <h4>Kurulum <em>{setupDone}/{setupSteps.length}</em></h4>
                <ol className="ap-steps" aria-label="Kurulum adımları">
                  {setupSteps.map((s, i) => (
                    <li key={s.short} className={`ap-step${s.done ? " done" : i === setupCurrent ? " cur" : ""}`} aria-current={i === setupCurrent ? "step" : undefined}>{s.short}</li>
                  ))}
                </ol>
                <ul className="ap-steplist">
                  {setupSteps.map((s, i) => (
                    <li key={s.short} className={s.done ? "done" : i === setupCurrent ? "cur" : ""}>
                      <span className="ap-stepmark" aria-hidden="true">{s.done ? I.check : i + 1}</span>
                      <div>
                        <b>{s.title}</b>
                        <small>{s.done ? s.doneText : s.todo}</small>
                      </div>
                    </li>
                  ))}
                </ul>
                {setupDone === setupSteps.length ? (
                  <div className="po-note ok">Kurulum tamam. Bağlantınızı paylaşabilirsiniz; gelen talepler “Onay bekleyenler” sekmesine düşer.</div>
                ) : (
                  <div className="po-note warn">Eksik adım varken sayfanız dışarıdan açılmaz ya da uygun saat görünmez.</div>
                )}
              </div>
            </div>
          </section>

          <section className="po-card">
            <div className="ap-body">
              <div className="po-form-sec">
                <h4>Randevu sayfanız</h4>
                <p className="po-form-hint">Bu bağlantıyı paylaştığınızda karşı taraf doğrudan takviminizi görür, giriş yapması gerekmez. Yayında olması için hem burayı hem de Ayarlar’daki genel randevu anahtarını açmanız gerekir.</p>

                <div className="po-grid2">
                  <div className="po-field">
                    <span>Yayın durumu</span>
                    <div className="po-seg" role="radiogroup" aria-label="Yayın durumu">
                      <button type="button" role="radio" aria-checked={profile.isPublic} className={profile.isPublic ? "on" : ""} onClick={() => setProfile({ ...profile, isPublic: true })}>Yayında</button>
                      <button type="button" role="radio" aria-checked={!profile.isPublic} className={!profile.isPublic ? "on" : ""} onClick={() => setProfile({ ...profile, isPublic: false })}>Kapalı</button>
                    </div>
                    <span className="po-fieldnote">{profile.isPublic ? "Bağlantınız dışarıdan açılabilir." : "Bağlantı dışarıdan açılmaz; mevcut randevular etkilenmez."}</span>
                  </div>
                  {profile.publicUrl && (
                    <div className="po-field">
                      <span>Bağlantınız</span>
                      <div className="ap-link">
                        <code>{profile.publicUrl}</code>
                        <button type="button" className="po-ib" aria-label="Bağlantıyı kopyala" title="Kopyala" onClick={copyLink}>{I.copy}</button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="po-grid2">
                  <label className="po-field">
                    <span>Bağlantı adı<em>küçük harf ve tire</em></span>
                    <input value={profile.slug} onChange={(e) => setProfile({ ...profile, slug: e.target.value })} />
                    <span className="po-fieldnote">Adresin sonundaki kısım.</span>
                  </label>
                  <label className="po-field">
                    <span>Görünen ünvan</span>
                    <input value={profile.displayTitle ?? ""} placeholder="Satış Sorumlusu" onChange={(e) => setProfile({ ...profile, displayTitle: e.target.value })} />
                  </label>
                </div>

                <label className="po-field">
                  <span>Kısa tanıtım</span>
                  <textarea rows={2} value={profile.intro ?? ""} placeholder="Numune ve sipariş görüşmeleri için randevu alabilirsiniz." onChange={(e) => setProfile({ ...profile, intro: e.target.value })} />
                </label>

                <div className="po-grid2">
                  <label className="po-field">
                    <span>Yüz yüze görüşme adresi</span>
                    <input value={profile.location ?? ""} onChange={(e) => setProfile({ ...profile, location: e.target.value })} />
                  </label>
                  <label className="po-field">
                    <span>Çevrim içi görüşme bağlantısı</span>
                    <input value={profile.defaultMeetingUrl ?? ""} placeholder="https://meet.google.com/…" onChange={(e) => setProfile({ ...profile, defaultMeetingUrl: e.target.value })} />
                    <span className="po-fieldnote">
                      {google?.connected
                        ? "Yedek oda. Google Takvim bağlı olduğu için her randevuya ayrı Meet bağlantısı üretilir; bu adres yalnız üretim başarısız olursa kullanılır."
                        : "Sabit odanız. Yalnız onayladığınız randevularda paylaşılır."}
                    </span>
                  </label>
                </div>
              </div>

              <div className="po-form-sec">
                <h4>Zamanlama kuralları</h4>
                <div className="po-grid4 ap-grid4">
                  <label className="po-field">
                    <span>En erken<em>saat sonrası</em></span>
                    <input type="number" min={0} value={profile.minNoticeHours} onChange={(e) => setProfile({ ...profile, minNoticeHours: Number(e.target.value) })} />
                    <span className="po-fieldnote">Son dakika randevusunu engeller.</span>
                  </label>
                  <label className="po-field">
                    <span>En fazla<em>gün ilerisi</em></span>
                    <input type="number" min={1} value={profile.maxAdvanceDays} onChange={(e) => setProfile({ ...profile, maxAdvanceDays: Number(e.target.value) })} />
                  </label>
                  <label className="po-field">
                    <span>Saat aralığı<em>dk</em></span>
                    <input type="number" min={5} step={5} value={profile.slotStepMinutes} onChange={(e) => setProfile({ ...profile, slotStepMinutes: Number(e.target.value) })} />
                    <span className="po-fieldnote">30 ise randevular 09:00, 09:30… diye başlar.</span>
                  </label>
                  <label className="po-field">
                    <span>Arası boşluk<em>dk</em></span>
                    <input type="number" min={0} step={5} value={profile.bufferMinutes} onChange={(e) => setProfile({ ...profile, bufferMinutes: Number(e.target.value) })} />
                  </label>
                </div>
              </div>

              <div className="po-actrow">
                <button type="button" className="po-btn primary push" onClick={saveProfile} disabled={busy}>{busy ? "Kaydediliyor" : "Sayfamı kaydet"}</button>
              </div>
            </div>
          </section>

          {google?.configured && (
            <section className="po-card">
              <div className="ap-body">
                <div className="po-form-sec">
                  <h4>Google Takvim</h4>
                  <p className="po-form-hint">Bağlarsanız onayladığınız her randevu kendi Google takviminize düşer ve görüşme için <b>o randevuya özel</b> bir Meet bağlantısı üretilir. Randevu iptal edilirse etkinlik takvimden de silinir.</p>
                  <div className="ap-google">
                    <span className="ap-google-ico" aria-hidden="true">{I.google}</span>
                    <div className="ap-google-text">
                      <b>
                        {google.connected ? (google.googleEmail || "Bağlı hesap") : "Bağlı değil"}
                        <span className={`po-badge ${!google.connected ? "muted" : google.lastError ? "warn" : "ok"}`}>{!google.connected ? "Bağlı değil" : google.lastError ? "Yenilenmeli" : "Bağlı"}</span>
                      </b>
                      <small>
                        {google.connected
                          ? [
                              google.connectedAt && `Bağlantı: ${new Date(google.connectedAt).toLocaleDateString("tr-TR", { dateStyle: "long" })}`,
                              google.lastSuccessAt && `Son başarılı işlem: ${new Date(google.lastSuccessAt).toLocaleDateString("tr-TR", { dateStyle: "long" })}`,
                            ].filter(Boolean).join(" · ") || "Bağlantı kuruldu."
                          : "Bağlamazsanız profilinizdeki sabit oda kullanılır."}
                      </small>
                    </div>
                    <div className="ap-google-acts">
                      {google.connected ? (
                        <>
                          <button type="button" className="po-btn small" onClick={connectGoogle}>Yeniden bağlan</button>
                          <button type="button" className="po-btn small danger" onClick={disconnectGoogle} disabled={busy}>Bağlantıyı kaldır</button>
                        </>
                      ) : (
                        <button type="button" className="po-btn primary" onClick={connectGoogle}>Google Takvim’e bağlan</button>
                      )}
                    </div>
                  </div>
                  {google.lastError && <div className="po-note err">{google.lastError}</div>}
                </div>
              </div>
            </section>
          )}

          <section className="po-card">
            <div className="ap-body">
              <div className="po-form-sec">
                <h4>Çalışma saatleriniz <em>{hours.length} gün açık</em></h4>
                <p className="po-form-hint">Kapalı bıraktığınız günlerde randevu alınamaz. Öğle arası için günü kapatmak yerine saatleri daraltın; bu ekran gün başına tek aralık tutar.</p>
                <div className="po-table-wrap tight ap-hours-wrap">
                  <table className="po-table small ap-hours">
                    <thead>
                      <tr><th>Gün</th><th>Açık</th><th>Başlangıç</th><th>Bitiş</th></tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4, 5, 6, 0].map((dow) => {
                        const row = hours.find((h) => h.dayOfWeek === dow);
                        return (
                          <tr key={dow} className={`static${row ? "" : " off"}`}>
                            <td className="po-name">{DAYS[dow]}</td>
                            <td>
                              <label className="po-check">
                                <input type="checkbox" checked={!!row} onChange={(e) => setDayHours(dow, e.target.checked)} />
                                <span className="po-hide-sm">{row ? "Açık" : "Kapalı"}</span>
                              </label>
                            </td>
                            <td><input type="time" className="ap-input" value={row?.startTime ?? "09:00"} disabled={!row} aria-label={`${DAYS[dow]} başlangıç`} onChange={(e) => patchDay(dow, { startTime: e.target.value })} /></td>
                            <td><input type="time" className="ap-input" value={row?.endTime ?? "18:00"} disabled={!row} aria-label={`${DAYS[dow]} bitiş`} onChange={(e) => patchDay(dow, { endTime: e.target.value })} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="po-actrow">
                  <button type="button" className="po-btn primary push" onClick={saveHours} disabled={busy}>{busy ? "Kaydediliyor" : "Saatleri kaydet"}</button>
                </div>
              </div>
            </div>
          </section>

          <section className="po-card">
            <div className="ap-body">
              <div className="po-form-sec">
                <h4>Kapalı günler <em>{exceptions.length}</em></h4>
                <p className="po-form-hint">İzin ve tatil günleri haftalık şablonunuzu ezer. Önümüzdeki 90 gün listelenir.</p>
                <div className="ap-addrow">
                  <label className="po-field">
                    <span>Kapatılacak gün</span>
                    <input type="date" value={excDraft.date} min={iso(new Date())} onChange={(e) => setExcDraft({ ...excDraft, date: e.target.value })} />
                  </label>
                  <label className="po-field">
                    <span>Gerekçe<em>isteğe bağlı</em></span>
                    <input value={excDraft.reason} maxLength={200} placeholder="İzin, tatil…" onChange={(e) => setExcDraft({ ...excDraft, reason: e.target.value })} />
                  </label>
                  <button type="button" className="po-btn" onClick={addException} disabled={busy || !excDraft.date}>{I.plus} Gün kapat</button>
                </div>
                {exceptions.length === 0 ? (
                  <div className="po-blank small"><b>Kapalı gün yok</b>Tüm günler haftalık şablonunuza göre açık.</div>
                ) : (
                  <div className="po-table-wrap tight">
                    <table className="po-table small">
                      <thead>
                        <tr><th>Tarih</th><th>Kapsam</th><th className="po-hide-sm">Gerekçe</th><th className="act" /></tr>
                      </thead>
                      <tbody>
                        {exceptions.map((x) => (
                          <tr key={x.availabilityExceptionId} className="static">
                            <td className="po-name">{new Date(x.date).toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "short" })}</td>
                            <td>{x.isClosed ? <span className="po-badge muted">Tüm gün</span> : `${x.startTime} – ${x.endTime}`}</td>
                            <td className="po-hide-sm po-sub">{x.reason || "—"}</td>
                            <td className="act">
                              <button type="button" className="po-ib danger" aria-label="Kapalı günü kaldır" title="Kaldır" onClick={() => removeException(x.availabilityExceptionId)} disabled={busy}>{I.trash}</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ============ RANDEVU TÜRLERİ ============ */}
      {tab === "types" && canSettings && (
        <div className="po-card">
          <div className="po-toolbar">
            <p className="po-hint" style={{ flex: "1 1 16rem" }}>Dışarıdaki kişi süre seçmez, tür seçer. Süre ve görüşme şekli türden gelir; bir türü hangi roller karşılıyorsa o roldeki herkes alabilir.</p>
            <button
              type="button"
              className="po-btn primary"
              onClick={() => setEditType({ name: "", durationMinutes: 30, modeOption: 3, isPublic: true, sortOrder: types.length, roleIds: [] })}
            >
              {I.plus} Yeni tür
            </button>
          </div>
          {types.length === 0 ? (
            <div className="po-blank">
              <span className="po-blank-icon">{I.calendar}</span>
              <b>Henüz tür tanımlanmamış</b>
              Misafirin seçeceği ilk randevu türünü ekleyin; süre ve görüşme şekli buradan gelir.
            </div>
          ) : (
            <div className="po-table-wrap">
              <table className="po-table">
                <thead>
                  <tr>
                    <th>Tür</th>
                    <th className="num">Süre</th>
                    <th className="po-hide-sm">Görüşme şekli</th>
                    <th>Görünürlük</th>
                    <th className="po-hide-md">Roller</th>
                    <th className="act">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map((t) => {
                    const names = t.roleIds.map((id) => roles.find((r) => r.roleId === id)?.name).filter(Boolean) as string[];
                    return (
                      <tr key={t.appointmentTypeId} onClick={() => setEditType({ ...t })}>
                        <td>
                          <div className="po-name">{t.name}</div>
                          {t.description && <div className="po-sub">{t.description}</div>}
                        </td>
                        <td className="num">{t.durationMinutes} dk</td>
                        <td className="po-hide-sm">{MODE_LABEL[t.modeOption]}</td>
                        <td><span className={`po-badge ${t.isPublic ? "ok" : "muted"}`}>{t.isPublic ? "Genel sayfada" : "Yalnız içeride"}</span></td>
                        <td className="po-hide-md">
                          {t.roleIds.length === 0 ? <span className="po-sub danger">Rol seçilmemiş</span> : (
                            <span className="ap-roletags">{(names.length ? names : [`${t.roleIds.length} rol`]).map((n) => <span key={n} className="po-badge muted">{n}</span>)}</span>
                          )}
                        </td>
                        <td className="act" onClick={(e) => e.stopPropagation()}>
                          <span className="po-rowacts">
                            <button type="button" className="po-ib" aria-label="Düzenle" title="Düzenle" onClick={() => setEditType({ ...t })}>{I.edit}</button>
                            <button type="button" className="po-ib danger" aria-label="Kaldır" title="Kaldır" onClick={() => removeType(t)} disabled={busy}>{I.trash}</button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============ KATMANLAR ============ */}
      {detail && (
        <AppointmentDetailModal
          a={detail}
          canUpdate={canUpdate}
          showStaff={showStaff}
          busy={busy}
          escDisabled={!!reasonAsk || confirm.isOpen}
          onClose={() => setDetail(null)}
          onApprove={approve}
          onReject={(a) => setReasonAsk({ kind: "reject", a })}
          onCancel={(a) => setReasonAsk({ kind: "cancel", a })}
          onMark={markStatus}
        />
      )}
      {reasonAsk && (
        <AppointmentReasonModal
          kicker={`${reasonAsk.a.number} · ${reasonAsk.a.guestName}`}
          title={reasonAsk.kind === "reject" ? "Talebi reddet" : "Randevuyu iptal et"}
          hint={reasonAsk.kind === "reject" ? "Örn. o saatte başka görüşmem var" : "Örn. toplantı ertelendi"}
          confirmText={reasonAsk.kind === "reject" ? "Reddet" : "İptal et"}
          busy={busy}
          onClose={() => setReasonAsk(null)}
          onSubmit={(reason) => (reasonAsk.kind === "reject" ? reject(reasonAsk.a, reason) : cancelAppointment(reasonAsk.a, reason))}
        />
      )}
      {editType && (
        <AppointmentTypeModal initial={editType} roles={roles} busy={busy} onClose={() => setEditType(null)} onSave={saveType} />
      )}
      {blockDraft && (
        <CalendarBlockModal initial={blockDraft} busy={busy} onClose={() => setBlockDraft(null)} onSave={saveBlock} />
      )}
      <ConfirmModal isOpen={confirm.isOpen} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} cancelText="Vazgeç" onConfirm={confirm.onConfirm} onCancel={closeConfirm} />
    </div>
  );
};

export default Appointments;
